import type { Dispatch, ReactNode, SetStateAction } from "react";
import { cx } from "../cx";
import { DepartmentFieldAccess } from "../departmentFields";
import type { UnsupportedRouteWarning } from "../applyExtracted";
import type { ExtractFillMap } from "../extractFill";
import { BoundedDate } from "../boundedDate";
import { todayISODate } from "../formModel";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { DepartmentConfig, FormState } from "../types";
import type { Errors } from "../validation";
import { RouteColumn } from "./RouteColumn";
import { TextInput } from "./fields";
import { WizardPage } from "./WizardPage";

class RouteDateBounds {
  pickupMax(form: FormState): string {
    return form.time.active ? form.time.deliveryDate.trim() : "";
  }

  unloadMin(form: FormState): string {
    return form.pickupDate.trim() || todayISODate();
  }

  unloadMax(form: FormState): string {
    return form.time.active ? form.time.deliveryDate.trim() : "";
  }
}

const dates = new RouteDateBounds();

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  config: DepartmentConfig;
  errors: Errors;
  hasErrors: boolean;
  highlighted: Set<string>;
  extractFill?: ExtractFillMap;
  unsupportedRoute: UnsupportedRouteWarning | null;
  onBlur: (id: string) => void;
  onBack: () => void;
  onContinue: () => void;
  backLabel?: ReactNode;
}

export function RouteOrderStep({
  form,
  setForm,
  config,
  errors,
  hasErrors,
  highlighted,
  extractFill,
  unsupportedRoute,
  onBlur,
  onBack,
  onContinue,
  backLabel,
}: Props) {
  const fields = new DepartmentFieldAccess(config);
  const copy = useQuoteCopy();
  const flash = (id: string) => highlighted.has(id) && "flash";
  const patch = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));
  const pickupMax = dates.pickupMax(form);
  const unloadMax = dates.unloadMax(form);

  return (
    <WizardPage
      title={copy.t("page.route")}
      hasErrors={hasErrors}
      onBack={onBack}
      backLabel={backLabel}
      onContinue={onContinue}
    >
      <section className="nv-section">
        <h3 className="nv-section-title">{copy.t("page.routeSection")}</h3>
        <p className="nv-field-hint">{fields.isPresent("loadStreet") ? copy.t("route.hintFullAddress") : copy.t("route.hintPostcode")}</p>
        {unsupportedRoute && (
          <p className="nv-form-error" role="alert">
            {copy.t("route.unsupported", {
              loadCountry: unsupportedRoute.loadCountry,
              unloadCountry: unsupportedRoute.unloadCountry,
            })}
          </p>
        )}
        <div className="nv-route">
          <RouteColumn
            direction="load"
            form={form}
            errors={errors}
            highlighted={highlighted}
            extractFill={extractFill}
            fields={fields}
            onPatch={patch}
            onBlur={onBlur}
          />
          <RouteColumn
            direction="unload"
            form={form}
            errors={errors}
            highlighted={highlighted}
            extractFill={extractFill}
            fields={fields}
            onPatch={patch}
            onBlur={onBlur}
          />
        </div>
      </section>

      <section className="nv-section">
        <TextInput
          id="nv-pickupDate"
          label={copy.t("route.pickupDate")}
          type="date"
          autoComplete="off"
          placeholder={copy.t("route.datePlaceholder")}
          required={fields.isRequired("pickupDate")}
          error={errors.pickupDate}
          fillStatus={extractFill?.status("pickupDate")}
          className={cx("nv-pickup", flash("pickupDate"))}
          value={form.pickupDate}
          min={todayISODate()}
          max={pickupMax || undefined}
          onChange={(v) => {
            patch({
              pickupDate: BoundedDate.capInput(v, todayISODate(), pickupMax),
            });
            onBlur("pickupDate");
          }}
          onBlur={() => {
            setForm((f) => ({
              ...f,
              pickupDate: BoundedDate.finishInput(f.pickupDate, todayISODate(), dates.pickupMax(f)),
            }));
            onBlur("pickupDate");
          }}
        />
        <p className="nv-field-hint nv-field-hint-below">{copy.t("route.specificDateHint")}</p>
        {fields.isPresent("unloadDate") && (
          <TextInput
            id="nv-unloadDate"
            label={copy.t("route.unloadDate")}
            type="date"
            autoComplete="off"
            placeholder={copy.t("route.datePlaceholder")}
            required={fields.isRequired("unloadDate")}
            error={errors.unloadDate}
            fillStatus={extractFill?.status("unloadDate")}
            className={cx("nv-pickup", flash("unloadDate"))}
            value={form.unloadDate}
            min={dates.unloadMin(form)}
            max={unloadMax || undefined}
            onChange={(v) => {
              patch({
                unloadDate: BoundedDate.capInput(v, dates.unloadMin(form), unloadMax),
              });
              onBlur("unloadDate");
            }}
            onBlur={() => {
              setForm((f) => ({
                ...f,
                unloadDate: BoundedDate.finishInput(f.unloadDate, dates.unloadMin(f), dates.unloadMax(f)),
              }));
              onBlur("unloadDate");
            }}
          />
        )}
      </section>
    </WizardPage>
  );
}
